/**
 * Chat Store - Zustand
 * ✅ ONLY UI State - No conversation data management
 * Conversation data lives in React Query
 */

import { create } from "zustand";
import type { UIMessage } from "ai";
import type { AgentType } from "@/types/chat.types";

interface ChatState {
  // UI State only
  activeConversationId: string | null;
  messages: UIMessage[];
  isStreaming: boolean;
  isGenerating: boolean;
  isLoadingHistory: boolean;
  currentAgentType: AgentType | null;
  isInEmptyState: boolean; // True when activeConversationId is null

  // UI Actions
  setActiveConversation: (id: string | null) => void;
  addMessage: (message: UIMessage) => void;
  updateMessage: (id: string, updates: Partial<UIMessage>) => void;
  setMessages: (messages: UIMessage[]) => void;
  clearMessages: () => void;
  setStreaming: (streaming: boolean) => void;
  setGenerating: (generating: boolean) => void;
  setLoadingHistory: (loading: boolean) => void;
  setCurrentAgentType: (type: AgentType | null) => void;
}

export const useChatStore = create<ChatState>((set) => ({
  // Initial UI state
  activeConversationId: null,
  messages: [],
  isStreaming: false,
  isGenerating: false,
  isLoadingHistory: false,
  currentAgentType: null,
  isInEmptyState: true, // Start in empty state

  // UI Actions
  setActiveConversation: (id) => {
    set({
      activeConversationId: id,
      isInEmptyState: id === null,
    });
    // Clear messages when switching to empty state
    if (id === null) {
      set({ messages: [], currentAgentType: null });
    }
  },

  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),

  updateMessage: (id, updates) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, ...updates } : msg
      ),
    })),

  setMessages: (messages) => set({ messages }),

  clearMessages: () => set({ messages: [], currentAgentType: null }),

  setStreaming: (streaming) => set({ isStreaming: streaming }),

  setGenerating: (generating) => set({ isGenerating: generating }),

  setLoadingHistory: (loading) => set({ isLoadingHistory: loading }),

  setCurrentAgentType: (type) => set({ currentAgentType: type }),
}));
